import React, { useMemo, useState } from 'react';
import { useWatermark, useFormatter } from './hooks';
import { ExitLogo } from './images';
import Root, { AsOfWrap, ConnectButton, ExitButton, ListStyled, MenuSubheader, OverlineText } from './shared-footer.styles';
import { Menu } from '../menu';
import { Icon } from '../icon';
import { ListItem } from '../list-item';
import { ListItemContent } from '../list-item-content';
import { IconButton } from '../icon-button';
const renderMenu = (items) => {
    const avaiableConnections = items.filter((item) => !item.isAuthorized);
    const connectedConnections = items.filter((item) => item.isAuthorized);
    return (React.createElement(ListStyled, { className: "menu-list" },
        avaiableConnections.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(MenuSubheader, null,
                React.createElement(OverlineText, null, "Available Connections")),
            avaiableConnections.map((item, index) => (React.createElement(ListItem, { key: index, button: true, onClick: item.connect },
                React.createElement(ListItemContent, null, item.text),
                item.connect && React.createElement(ConnectButton, null, "Connect")))))),
        connectedConnections.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(MenuSubheader, null,
                React.createElement(OverlineText, null, "Connected")),
            connectedConnections.map((item, index) => (React.createElement(ListItem, { key: index, button: true, onClick: item.select },
                React.createElement(ListItemContent, null, item.text))))))));
};
/**
 * @param {string} className - An optional class name to apply to the root element.
 * @param {ElementType} as - An optional styled component element for customization.
 * @param {WatermarksT} provider - The watermark provider to use. This is a required prop.
 * @param {() => void} onExit - An optional callback function to handle exit actions. This function is called when the exit event is triggered.
 * @param {string} date - An optional date string to display.
 * @param {'ny-ago' | 'as-of' | FormatterFunctionT} formatter - An optional format type or function for formatting dates. Can be 'ny-ago', 'as-of', or a custom formatter function.
 * @param {CustomLogoT} customLogo - An optional function that returns a custom logo as a React element. If not provided, a default logo will be used.
 * @param {string | React.JSX.Element} customActionIcon - An optional custom action icon instead of the default exit icon. It could be an URL or a React element.
 * @param {ConnectionInstanceT[]} instances - An optional array of provider instances to display in the footer.
 * @param {InstanceActionT} instanceAction - An optional object with a connect functions to handle instance actions.
 */
const SharedFooter = ({ as, className = '', provider, onExit, date, formatter, customLogo, customActionIcon, cdnUrl = 'https://inventwidgetstorage.z19.web.core.windows.net/icons/crm-providers', instances, instanceAction = {
    connect: undefined,
    select: undefined
} }) => {
    const { Watermark, themeAccent } = useWatermark({ provider, customLogo, cdnUrl });
    const updatedAt = useFormatter({ date, formatter });
    let EndIcon;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const handleToggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };
    const handleCloseMenu = () => {
        setIsMenuOpen(false);
    };
    const menuItems = useMemo(() => (instances === null || instances === void 0 ? void 0 : instances.filter((instance) => instance.providerType.toLowerCase() === (provider === null || provider === void 0 ? void 0 : provider.toLowerCase())).map((instance) => ({
        text: instance.instanceName,
        isAuthorized: instance.isAuthorized,
        connect: (instanceAction === null || instanceAction === void 0 ? void 0 : instanceAction.connect)
            ? () => { var _a; return (_a = instanceAction === null || instanceAction === void 0 ? void 0 : instanceAction.connect) === null || _a === void 0 ? void 0 : _a.call(instanceAction, instance.configurationId); }
            : undefined,
        select: (instanceAction === null || instanceAction === void 0 ? void 0 : instanceAction.select) ? () => { var _a; return (_a = instanceAction === null || instanceAction === void 0 ? void 0 : instanceAction.select) === null || _a === void 0 ? void 0 : _a.call(instanceAction, instance); } : undefined
    }))) || [], [instances, instanceAction]);
    const MenuContent = useMemo(() => renderMenu(menuItems), [menuItems]);
    if (typeof customActionIcon === 'string') {
        EndIcon = React.createElement("img", { src: customActionIcon, alt: "Custom action icon" });
    }
    else if (customActionIcon) {
        EndIcon = customActionIcon;
    }
    else {
        EndIcon = React.createElement(ExitLogo, null);
    }
    const onExitHd = () => {
        if (onExit)
            onExit();
    };
    return (React.createElement(Root, { as: as, className: className, "$withExit": Boolean(onExit) },
        React.createElement(AsOfWrap, null, updatedAt),
        React.createElement(Watermark, { themeAccent: themeAccent }),
        menuItems && menuItems.length > 1 && (React.createElement(Menu, { content: MenuContent, isOpen: isMenuOpen, onClickOutside: handleCloseMenu },
            React.createElement(IconButton, { onClick: handleToggleMenu },
                React.createElement(Icon, { name: "settings_outline", "aria-label": "Connect" })))),
        onExit && React.createElement(ExitButton, { variant: "transparent", onClick: onExitHd, EndIcon: EndIcon })));
};
export default SharedFooter;
